import type { Category, Listing, Review, BlogPost, BlogTag } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/.netlify/functions';

// Categories
export async function fetchCategories(): Promise<Category[]> {
  try {
    const res = await fetch(`${API_BASE}/categories`);
    if (!res.ok) throw new Error('Failed to fetch categories');
    return res.json();
  } catch {
    return getLocalCategories();
  }
}

// Regions (name + listing count, only regions that have listings)
export async function fetchRegions(): Promise<{ name: string; count: number }[]> {
  try {
    const res = await fetch(`${API_BASE}/regions`);
    if (!res.ok) throw new Error('Failed to fetch regions');
    return res.json();
  } catch {
    return [];
  }
}

// Listings
export async function fetchListings(params?: {
  category?: string;
  region?: string;
  search?: string;
  sort?: string;
}): Promise<Listing[]> {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.region) query.set('region', params.region);
  if (params?.search) query.set('search', params.search);
  if (params?.sort) query.set('sort', params.sort);

  try {
    const res = await fetch(`${API_BASE}/listings?${query}`);
    if (!res.ok) throw new Error('Failed to fetch listings');
    return res.json();
  } catch {
    return getLocalListings(params);
  }
}

export async function fetchListingById(idOrSlug: number | string): Promise<Listing | null> {
  const raw = String(idOrSlug ?? '').trim();
  const isNumeric = /^\d+$/.test(raw);
  try {
    const param = isNumeric ? `id=${encodeURIComponent(raw)}` : `slug=${encodeURIComponent(raw)}`;
    const res = await fetch(`${API_BASE}/listing?${param}`);
    if (!res.ok) throw new Error('Failed to fetch listing');
    return res.json();
  } catch {
    return isNumeric ? getLocalListingById(Number(raw)) : getLocalListingBySlug(raw);
  }
}

export async function fetchListingBySlug(slug: string): Promise<Listing | null> {
  try {
    const res = await fetch(`${API_BASE}/listing?slug=${encodeURIComponent(slug)}`);
    if (!res.ok) throw new Error('Failed to fetch listing');
    return res.json();
  } catch {
    return getLocalListingBySlug(slug);
  }
}

export async function createListing(data: Partial<Listing>): Promise<{ success: boolean; id?: number; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/listings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const result = await res.json();
    return result;
  } catch {
    return { success: false, error: 'Network error' };
  }
}

// Blog (Marine News)
export async function fetchBlogPosts(tag?: string, limit = 24): Promise<BlogPost[]> {
  try {
    const query = new URLSearchParams();
    if (tag) query.set('tag', tag);
    query.set('limit', String(limit));
    const res = await fetch(`${API_BASE}/blog?${query}`);
    if (!res.ok) throw new Error('Failed to fetch blog posts');
    return res.json();
  } catch {
    return [];
  }
}

export async function fetchBlogPost(slug: string): Promise<BlogPost | null> {
  try {
    const res = await fetch(`${API_BASE}/blog?slug=${encodeURIComponent(slug)}`);
    if (!res.ok) throw new Error('Failed to fetch blog post');
    return res.json();
  } catch {
    return null;
  }
}

export async function fetchBlogTags(): Promise<BlogTag[]> {
  try {
    const res = await fetch(`${API_BASE}/blog?tags=1`);
    if (!res.ok) throw new Error('Failed to fetch blog tags');
    return res.json();
  } catch {
    return [];
  }
}

// Reviews
export async function fetchReviews(listingId: number): Promise<Review[]> {
  try {
    const res = await fetch(`${API_BASE}/reviews?listing_id=${listingId}`);
    if (!res.ok) throw new Error('Failed to fetch reviews');
    return res.json();
  } catch {
    return [];
  }
}

export async function createReview(data: { listing_id: number; reviewer_name: string; rating: number; text: string }): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const result = await res.json();
    return result;
  } catch {
    return { success: false, error: 'Network error' };
  }
}

// Fallback local data (from seed) for dev/preview without API
import { seedCategories, seedListings } from '../data/seed';

function getLocalCategories(): Category[] {
  return seedCategories;
}

function getLocalListingById(id: number): Listing | null {
  return seedListings.find(l => l.id === id) || null;
}

function getLocalListingBySlug(slug: string): Listing | null {
  // Generate slug from title to match
  const makeSlug = (title: string) => title.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/--+/g, '-');
  return seedListings.find(l => makeSlug(l.title) === slug) || null;
}

function getLocalListings(params?: { category?: string; region?: string; search?: string; sort?: string }): Listing[] {
  let results = [...seedListings];

  if (params?.category) results = results.filter(l => l.category_id === params.category);
  if (params?.region) results = results.filter(l => l.region_name === params.region);
  if (params?.search) {
    const q = params.search.toLowerCase();
    results = results.filter(l =>
      l.title.toLowerCase().includes(q) ||
      l.category_name?.toLowerCase().includes(q) ||
      l.region_name?.toLowerCase().includes(q) ||
      l.description.toLowerCase().includes(q) ||
      l.services.some(s => s.toLowerCase().includes(q))
    );
  }

  if (params?.sort === 'name') {
    results.sort((a, b) => a.title.localeCompare(b.title));
  } else if (params?.sort === 'reviews') {
    results.sort((a, b) => b.reviews_count - a.reviews_count);
  } else {
    results.sort((a, b) => b.rating - a.rating);
  }

  return results;
}
